import React from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";

import type { MeterUploadRowData } from "@utils/bulkMeterUpload/buildTrialRunRow";
import {
  commitSubscriberSelection,
  resolveCurrentOption,
  type SubscriberOption,
} from "@utils/bulkMeterUpload/subscriberSelect";

type SubscriberSelectCellProps = {
  rowData: MeterUploadRowData;
  // Scheme-scoped subscriber options, fetched once by TrialRun and shared across all
  // rows (one lookup, not one per row). Each option's value is the account number it
  // fills onto the row.
  options: SubscriberOption[];
  // Non-ledgered rows can re-select; ledgered rows render the account read-only.
  editable: boolean;
  // The customer the selected offtake is actually registered to (trial.subscriberName,
  // server truth keyed by Offtake ID). Surfaced as an inline hint when it disagrees with
  // the picked account, naming who the offtake should belong to.
  ownerName?: string;
  // True when the picked account ≠ the offtake's owner (errors.accountNumber). Drives the
  // mismatch hint; the trial run remains the source of this signal, not this cell.
  mismatch?: boolean;
  // Commits the patched rowData through the same path as the value cells
  // (useBulkMeterUploadFlow.editRow keeps the local-vs-persist decision). accountNumber
  // is in trialRunSig, so a selection re-runs the trial and the mismatch hint
  // (driven by the trial result) updates.
  onCommit: (rowData: MeterUploadRowData) => void | Promise<void>;
};

// The account cell as a name-first searchable subscriber select. Picking a customer
// fills the account number; the trial run remains the source of validation truth. A
// parsed/draft account with no lookup match still renders as the current value and
// can be replaced (resolveCurrentOption returns a synthetic option).
const SubscriberSelectCell: React.FunctionComponent<SubscriberSelectCellProps> = ({
  rowData,
  options,
  editable,
  ownerName,
  mismatch,
  onCommit,
}) => {
  const { t } = useTranslation();

  if (!editable) {
    return <span>{rowData.accountNumber}</span>;
  }

  const current = resolveCurrentOption(rowData.accountNumber, options);

  return (
    <div className="min-w-[12rem]">
      <Select<SubscriberOption>
        classNamePrefix="subscriber_select"
        className="text-sm"
        placeholder={t("bulk_meter_upload.subscriber.placeholder")}
        noOptionsMessage={() => t("bulk_meter_upload.subscriber.no_options")}
        options={options}
        value={current}
        onChange={option => onCommit(commitSubscriberSelection(rowData, option))}
        isClearable
        aria-label={t("subscriber.name") || undefined}
        // Portal the menu out of the scrolling table so it isn't clipped by the cell.
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : undefined
        }
        menuPosition="fixed"
      />
      {mismatch && ownerName ? (
        <p className="mt-1 text-xs text-red-600">
          {t("bulk_meter_upload.subscriber.mismatch_hint", { name: ownerName })}
        </p>
      ) : null}
    </div>
  );
};

export default SubscriberSelectCell;
